import { AssetsTab } from "./CloudAssetsTabs/AssetsTab";
import { Link } from "components/buttons/Link";
import { usePageHeader } from "hooks/usePageHeader";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useSwitcherClientCore } from "hooks/useSwitcherClient";
import { Tabs } from "components/tabs/Tabs";
import { splitAssetsByType, productionGroupParser } from "helpers/assetHelper";
import { useSelector } from "react-redux";
import { RootState } from "store/reducers";
import {
    ProgressBar,
    ProgressBarProps
} from "components/widgets/progress-bar/ProgressBar";
import styles from "./index.module.scss";
import { DragDrop } from "@uppy/react";
import { useIsMobile } from "hooks/useIsMobile";
import { AssetUploadModal } from "components/modal/AssetUploadModal";
import Uppy from "@uppy/core";
import { useCreateAssetAndUpload } from "hooks/useCreateAssetAndUpload";
import rollbar from "helpers/rollbar";
import { closeCurrentModal, setActiveModal } from "store/modal/slice";
import { AppDispatch } from "store/store";
import { useDispatch } from "react-redux";
import { Modals } from "store/modal/types";
import { LoadingOverlay } from "components/loading-overlay";
import { MaintenanceNotice } from "components/maintenance-plan/MaintenanceNotice";

export enum CloudAssetsTabs {
    Assets = "assets",
    RawFiles = "raw-files"
}

export const CloudAssetsPageV2: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const { userInfo } = useSelector((state: RootState) => state.user);
    const { isMobile } = useIsMobile();

    const [activeTab, setActiveTab] = useState<CloudAssetsTabs>(
        CloudAssetsTabs.Assets
    );

    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [isPostUploadRefreshing, setIsPostUploadRefreshing] =
        useState<boolean>(false);

    const {
        data: allAssets,
        loading,
        dispatchApiRequest: refetchAssets
    } = useSwitcherClientCore((client) => client.userAssets_GetByUserAsync_v2, {
        requestImmediately: true
    });

    const maxAssets =
        userInfo?.Claims?.Details?.FeatureLimitClaims?.["cloudassets:maxcount"];

    const assetCount = useMemo(() => {
        if (!allAssets || !Array.isArray(allAssets)) return undefined;
        return allAssets.filter((a) => {
            if (a.Details.Type !== "Art")
                return !(a.RequiredByAssetIds?.length > 0);
            else return true;
        }).length;
    }, [allAssets]);

    const styleVariant = useMemo<ProgressBarProps["variant"]>(() => {
        if (assetCount >= maxAssets) {
            return "video-storage-max";
        }
        if (assetCount / maxAssets >= 0.8) {
            return "video-storage-medium";
        }
        return "thick";
    }, [assetCount, maxAssets]);

    const { assets, rawFiles } = useMemo(() => {
        return splitAssetsByType(allAssets);
    }, [allAssets]);

    usePageHeader({
        title: t("cloud-assets:title"),
        subTitle: (
            <>
                <p>
                    {t("cloud-assets:description")}
                    <Link
                        to="https://itunes.apple.com/us/app/switcher-studio-pro/id908386221"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t("cloud-assets:download-switcher-studio")}
                    </Link>
                </p>
                <MaintenanceNotice page="cloud-assets" />
            </>
        )
    });

    const uppy = useMemo(
        () =>
            new Uppy({
                restrictions: {
                    allowedFileTypes: ["image/*", "video/*", "audio/*"]
                },
                autoProceed: false // Don't upload immediately
            }),
        []
    );

    const getAssetById = useCallback(
        (id: string) => {
            return allAssets?.find((asset) => asset.Details.Id === id);
        },
        [allAssets]
    );

    const closeModal = useCallback(() => {
        dispatch(closeCurrentModal());
    }, [dispatch]);

    const { dispatchApiRequest: createNewAssets } = useSwitcherClientCore(
        (client) => client.userAssets_PutUserAssets_v2,
        { suppressErrorBoundary: true, requestImmediately: false }
    );

    const handleModalClose = useCallback(() => {
        // Always close the modal.
        closeModal();

        // Only clear the Uppy selection when we're NOT uploading.
        // If we cancel while uploading, we may interrupt the upload and the page overlay won't show as intended.
        if (!isUploading) {
            uppy.cancelAll();
        }
    }, [closeModal, isUploading, uppy]);

    const { uploadFilesWithSignedUrls } = useCreateAssetAndUpload({
        createNewAssets,
        onUploadComplete: async () => {
            // Keep overlay up while the list/thumbnails catch up after upload
            setIsPostUploadRefreshing(true);

            await refetchAssets();

            setIsPostUploadRefreshing(false);

            // Upload is confirmed complete by the server → stop the upload spinner now
            setIsUploading(false);

            // Safe cleanup after the whole pipeline completes
            uppy.cancelAll();
        }
    });

    const allProductionGroups = useMemo(() => {
        if (!allAssets) return [];
        return productionGroupParser(allAssets);
    }, [allAssets]);

    const dragAndDropLocaleStrings = useMemo(() => {
        // `%{browse}` is replaced with a link that opens the system file selection dialog.
        const dropHereOr = `%{browse} ${
            !isMobile ? t("video-upload:drag-drop") : ""
        }`;

        // Determine the label for the link that opens the system file selection dialog
        let browse: string;
        browse = isMobile
            ? t("cloud-assets:upload-tap")
            : t("cloud-assets:upload-click");

        return { strings: { dropHereOr, browse } };
    }, [isMobile, t]);

    const handleUpload = useCallback(
        (files: any[], fileAttributes: { [fileName: string]: any }) => {
            // Start overlay immediately and keep it up until the server confirms completion
            setIsUploading(true);

            // Close modal right away so the user sees the page-level spinner overlay during upload
            closeModal();

            uploadFilesWithSignedUrls(files, fileAttributes).catch((error) => {
                rollbar.error("Error uploading assets", error);

                // On failure, stop spinner and cleanup
                setIsUploading(false);
                uppy.cancelAll();
            });
        },
        [closeModal, uploadFilesWithSignedUrls, uppy]
    );

    const openModal = useCallback(
        (files: any[]) => {
            dispatch(
                setActiveModal({
                    id: Modals.AssetUploadModal,
                    type: Modals.AssetUploadModal,
                    component: (
                        <AssetUploadModal
                            onClose={handleModalClose}
                            files={files}
                            onUpload={handleUpload}
                            isUploading={isUploading}
                            allProductionGroups={allProductionGroups}
                        />
                    )
                })
            );
        },
        [
            handleModalClose,
            handleUpload,
            dispatch,
            isUploading,
            allProductionGroups
        ]
    );

    const onFilesAdded = useCallback(
        (addedFiles: any[]) => {
            if (addedFiles.length > 0) {
                // Some environments emit "files-added" multiple times; prefer the full uppy list.
                // If it's empty due to timing/batching, fall back to the event payload.
                const allFiles = uppy.getFiles();
                openModal(allFiles.length > 0 ? allFiles : addedFiles);
            }
        },
        [openModal, uppy]
    );

    useEffect(() => {
        uppy.on("files-added", onFilesAdded);
        return () => {
            uppy.off("files-added", onFilesAdded);
        };
    }, [uppy, onFilesAdded]);

    const showOverlay = isUploading || isPostUploadRefreshing;

    return (
        <LoadingOverlay
            isLoading={showOverlay}
            transparentOnLoad={true}
            transparentOnFirstLoad={true}
        >
            <div>
                <div>
                    <DragDrop
                        width="100%"
                        height="100%"
                        uppy={uppy}
                        locale={dragAndDropLocaleStrings}
                        note={t("cloud-assets:upload-restrictions")}
                        className={styles["compact-upload"]}
                    />
                </div>

                {assetCount !== undefined && (
                    <ProgressBar
                        currentValue={assetCount}
                        maxValue={maxAssets || 0}
                        variant={styleVariant}
                    />
                )}

                <Tabs<CloudAssetsTabs>
                    tabs={[
                        {
                            id: CloudAssetsTabs.Assets,
                            label: "Assets",
                            component: (
                                <AssetsTab
                                    assets={assets}
                                    loading={loading}
                                    refetchAssets={refetchAssets}
                                    variant="all-assets"
                                    allProductionGroups={allProductionGroups}
                                    getAssetById={getAssetById}
                                />
                            )
                        },
                        {
                            id: CloudAssetsTabs.RawFiles,
                            label: "Raw Files",
                            component: (
                                <AssetsTab
                                    assets={rawFiles}
                                    loading={loading}
                                    refetchAssets={refetchAssets}
                                    variant="raw-files"
                                    allProductionGroups={allProductionGroups}
                                    getAssetById={getAssetById}
                                />
                            )
                        }
                    ]}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                />
            </div>
        </LoadingOverlay>
    );
};
